import { AddIcon } from "@minket/brand/icons/add";
import { AutomationsIcon } from "@minket/brand/icons/automations";
import { Button } from "@minket/ui/components/button";
import { EmptyState } from "@minket/ui/components/empty-state";
import { StatusBadge } from "@minket/ui/components/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@minket/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import {
  type AutomationItem,
  automationStatus,
  triggerText,
  versionLabel,
} from "@/components/automations/format";
import { UnavailableAction } from "@/components/page/action";
import { GhostTable } from "@/components/page/ghost";
import { Page } from "@/components/page/page";
import { Scoped } from "@/components/page/scope";
import { QueryView } from "@/components/page/states";
import { type BodyOf, endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiKey, apiQuery, apiRequest } from "@/lib/query";
import { notify } from "@/lib/toast";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/automations/")({
  component: Automations,
});

const CreateAutomation = lazy(() =>
  import("@/components/automations/create-automation.ondemand").then((m) => ({
    default: m.CreateAutomationDialog,
  })),
);

const columns = ["Status", "Automation", "Version", "Runs"] as const;
const PAGE = 50;
const will = "Each automation will be listed here with its trigger, its version and its runs.";

function Automations() {
  const { project } = useWorkspace();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [after, setAfter] = useState<string | undefined>();
  const [creating, setCreating] = useState(false);
  const projectId = project?.id ?? "";
  const list = useQuery({
    ...apiQuery(endpoints.automations, { projectId }, { limit: PAGE, after }),
    enabled: project !== null,
  });
  const create = useMutation({
    mutationFn: (body: BodyOf<typeof endpoints.createAutomation>) =>
      apiRequest(endpoints.createAutomation, { projectId }, { body }),
    onSuccess: (automation) => {
      void queryClient.invalidateQueries({
        queryKey: apiKey(endpoints.automations, { projectId }),
      });
      notify("success", "Draft created. Add its steps, then publish it.");
      setCreating(false);
      void navigate({ to: "/automations/$id", params: { id: automation.id } });
    },
  });
  const canManage = useCan("automations.manage");
  const preview = (busy: boolean) => (
    <GhostTable columns={columns} label="automations" busy={busy} />
  );

  return (
    <Page
      title="Automations"
      action={
        project && canManage ? (
          <Button onClick={() => setCreating(true)}>
            <AddIcon size={20} className="size-5" />
            New automation
          </Button>
        ) : (
          <UnavailableAction
            label="New automation"
            icon={AddIcon}
            reason={
              project ? "Viewers can read automations but not create them" : "Needs a project"
            }
          />
        )
      }
    >
      <Scoped scope="project" will={will} icon={<AutomationsIcon />} preview={preview}>
        {() => (
          <QueryView query={list} noun="automations" preview={preview}>
            {(page) =>
              page.data.length === 0 && after === undefined ? (
                <EmptyState
                  data-state="empty"
                  icon={<AutomationsIcon />}
                  title="No automations yet"
                  description="Draw the steps from a trigger: sends, waits and conditions. Each published version keeps its runs."
                  preview={preview(false)}
                />
              ) : (
                <div className="grid min-w-0 gap-3">
                  <AutomationTable rows={page.data} />
                  {page.has_more || after !== undefined ? (
                    <nav aria-label="Pages of automations" className="flex justify-end gap-2">
                      {after !== undefined ? (
                        <Button variant="outline" size="sm" onClick={() => setAfter(undefined)}>
                          Newest
                        </Button>
                      ) : null}
                      {page.has_more ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAfter(page.data.at(-1)?.id)}
                        >
                          Older
                        </Button>
                      ) : null}
                    </nav>
                  ) : null}
                </div>
              )
            }
          </QueryView>
        )}
      </Scoped>
      {creating && project ? (
        <Suspense fallback={null}>
          <CreateAutomation
            pending={create.isPending}
            error={create.error}
            onCreate={(body) => create.mutate(body)}
            onClose={() => {
              setCreating(false);
              create.reset();
            }}
          />
        </Suspense>
      ) : null}
    </Page>
  );
}

function AutomationTable({ rows }: { rows: readonly AutomationItem[] }) {
  return (
    <div className="@container min-w-0">
      <Table aria-label="Automations, newest first">
        <TableHeader>
          <TableRow>
            <TableHead className="@max-[34rem]:hidden">Status</TableHead>
            <TableHead>Automation</TableHead>
            <TableHead className="@max-[44rem]:hidden">Version</TableHead>
            <TableHead className="text-right">Runs</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const status = automationStatus(row.status);
            const lamp = <StatusBadge tone={status.tone}>{status.label}</StatusBadge>;
            return (
              <TableRow key={row.id}>
                <TableCell className="whitespace-nowrap @max-[34rem]:hidden">{lamp}</TableCell>
                <TableCell className="w-full max-w-0">
                  <Link
                    to="/automations/$id"
                    params={{ id: row.id }}
                    className="block w-fit max-w-full rounded-sm font-medium wrap-break-word text-foreground decoration-1 underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                  >
                    {row.name}
                  </Link>
                  <span className="mt-0.5 flex min-w-0 flex-wrap items-baseline gap-x-3 text-[12.5px] text-muted-foreground">
                    <span className="min-w-0 wrap-break-word">{triggerText(row.trigger)}</span>
                    <span className="hidden text-[13px] text-foreground @max-[34rem]:inline">
                      {lamp}
                    </span>
                  </span>
                </TableCell>
                <TableCell className="type-data text-[13px] whitespace-nowrap @max-[44rem]:hidden">
                  {versionLabel(row)}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <span className="type-data text-[18px] leading-6">
                    {row.stats.runs_active.toLocaleString("en-GB")}
                  </span>
                  <span className="text-[12.5px] text-muted-foreground"> active</span>
                  <span className="type-data block text-[12.5px] text-muted-foreground">
                    {row.stats.runs_total.toLocaleString("en-GB")} total
                    {row.stats.runs_failed > 0
                      ? `, ${row.stats.runs_failed.toLocaleString("en-GB")} failed`
                      : ""}
                  </span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
